import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";

export interface ChipPlaceholder {
  text: string;
  value: string;
}

export interface ChipBody {
  content: string;
  chips: ChipPlaceholder[];
}

export interface InputFocusOut {
  id: string;
  value: string;
}

@Component({
    selector: "tidy-chip-text-area",
    templateUrl: "chip-text-area.html",
    styleUrls: ["./chip-text-area.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ChipTextArea implements OnInit, OnChanges {
  @Input() id: string;
  @Input() inputTitle: string;
  @Input() placeholder: ChipPlaceholder;
  @Input() body: ChipBody;
  @Input() inputKey: string;
  @Input() isRequired: boolean;
  @Input() readonly = false;
  @Input() applyFormatting = true;
  @Output() inputChange = new EventEmitter<InputFocusOut>();
  @Output() inputFocusIn = new EventEmitter<InputFocusOut>();
  @Output() inputFocusOut = new EventEmitter<InputFocusOut>();
  @Output() chipInit = new EventEmitter<void>();
  innerHtml: string;
  placeholders: ChipPlaceholder[] = [];
  control: FormControl;
  private lastRange: Range | null = null;

  constructor(private renderer: Renderer2, private el: ElementRef) {}

  ngOnInit(): void {
    this.control = new FormControl('', this.isRequired ? Validators.required : null);
    this.watchForPaste();
    this.watchForInputChanges();
    this.watchForInputFocusIn();
    this.watchForFocusOut();
    this.chipInit.emit();
  }

  ngOnChanges(event: SimpleChanges): void {
    if (event?.isRequired) {
      if (!this.control) {
        this.control = new FormControl('', event.isRequired.currentValue ? Validators.required : null);
      } else {
        this.control.setValidators(event.isRequired.currentValue ? Validators.required : null);
      }
    }
    if (this.placeholder && this.placeholder?.text && this.placeholder?.value) {
      this.appendPlaceholder(this.placeholder);
    }
    if (this.body) {
      this.parseAndSetBody();
    }
    if (event?.applyFormatting) {
      this.applyFormatting = event.applyFormatting.currentValue;
    }
  }

  watchForPaste(): void {
    setTimeout(() => {
      const container = this.el.nativeElement.querySelector(`#customMessage-${  this.id}`);
      container.addEventListener("paste", (event) => {
        // Prevent non-text content
        const items = (event.clipboardData || event.originalEvent.clipboardData).items;
        for (const index in items) {
          const item = items[index];
          if (item.type !== undefined && item.type !== "text/plain" && item.type !== "text/html") {
            event.preventDefault();
            break;
          }
        }

        // Handle formatting
        if (!this.applyFormatting) {
          event.preventDefault();
          // Get plain text content
          const text = event.clipboardData.getData('text/plain');
          // Create a temporary div to strip any remaining HTML
          const tempDiv = document.createElement('div');
          tempDiv.textContent = text;
          const plainText = tempDiv.textContent || tempDiv.innerText || '';

          // Insert the plain text at cursor position
          const selection = window.getSelection();
          if (selection?.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(plainText));
            // Move cursor to end of inserted text
            range.setStartAfter(range.endContainer);
            range.setEndAfter(range.endContainer);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
        this.parseAndEmitInnerHtml();
      });
    }, 1000);
  }

  watchForInputFocusIn(): void {
    setTimeout(() => {
      const container = this.el.nativeElement.querySelector(`#customMessage-${  this.id}`);
      container.addEventListener("focusin", (_event) => {
        this.inputFocusIn.emit({ id: this.id, value: this.innerHtml });
      });
    }, 1000);
  }

  watchForFocusOut(): void {
    setTimeout(() => {
      const container = this.el.nativeElement.querySelector(`#customMessage-${  this.id}`);
      container.addEventListener("focusout", (_event) => {
        const selection = window.getSelection();
        if (selection && selection?.rangeCount > 0) {
          this.lastRange = selection.getRangeAt(0).cloneRange();
        }
        this.inputFocusOut.emit({ id: this.id, value: this.innerHtml });
      });
    }, 1000);
  }

  watchForInputChanges(): void {
    let typingTimer: any;
    const doneTypingInterval = 1000;

    setTimeout(() => {
      const container = this.el.nativeElement.querySelector(`#customMessage-${  this.id}`);
      container.addEventListener("input", (_event) => {
        clearTimeout(typingTimer); // Clear the timer if the user is still typing
        typingTimer = setTimeout(() => {
          this.parseAndEmitInnerHtml(); // User is considered done typing after 1 second, emit the innerHtml
        }, doneTypingInterval);
      });
    }, 1000);
  }

  parseAndEmitInnerHtml(): void {
    const innerHtml =
      this.el.nativeElement.querySelector(`#customMessage-${  this.id}`).innerHTML;

    const parser = new DOMParser();
    const doc = parser.parseFromString(innerHtml, "text/html");
    const chips = doc.querySelectorAll("ion-chip");

    chips.forEach((chip) => {
      const regex = /{([^}]+)}/g;
      const matches = chip.id.match(regex);
      const textNode = doc.createTextNode(matches ? chip.id : `{${chip.id}}`);
      chip.parentNode.replaceChild(textNode, chip);
    });

    let newHtml = doc.body.innerHTML;
    newHtml = newHtml.replace(/&nbsp;/g, " ");

    // Convert empty divs back to br tags (DOMParser converts br to div)
    newHtml = newHtml.replace(/<div><\/div>/g, '<br>');

    // Remove trailing <br> tag if present
    if (newHtml.endsWith('<br>')) {
      newHtml = newHtml.slice(0, -4);
    }

    // Remove trailing whitespace (spaces, tabs, newlines)
    newHtml = newHtml.replace(/\s+$/, '');

    this.innerHtml = newHtml;
    this.inputChange.emit({ id: this.id, value: this.innerHtml });
    this.control.setValue(this.innerHtml);
  }

  removePlaceholder(span: any): void {
    this.renderer.removeChild(
      this.el.nativeElement.querySelector(`#customMessage-${  this.id}`),
      span
    );
    setTimeout(() => {
      this.parseAndEmitInnerHtml();
    }, 1000);
  }

  appendPlaceholder(field: ChipPlaceholder): void {
    // Create ion-chip element
    const ionChip = this.renderer.createElement("ion-chip");

    // Create span for the field text
    const span = this.renderer.createElement("span");
    const text = this.renderer.createText(`${field.text}`);
    this.renderer.appendChild(span, text);

    this.renderer.appendChild(ionChip, span);
    if (!this.readonly) {
      // Create ion-icon element for the remove icon
      const ionIcon = this.renderer.createElement("ion-icon");
      this.renderer.setAttribute(ionIcon, "name", "close-circle");
      this.renderer.listen(ionIcon, "click", () =>
        this.removePlaceholder(ionChip)
      );

      // Append the span and ion-icon to the ion-chip
      this.renderer.appendChild(ionChip, ionIcon);
    } else {
      this.renderer.addClass(ionChip, "fix-padding");
    }

    // Add attributes and classes to the ion-chip
    this.renderer.setAttribute(ionChip, "contenteditable", "false");
    this.renderer.setAttribute(ionChip, "id", field.value);
    this.renderer.addClass(ionChip, "custom-chip");

    // Get the container
    const container = this.el.nativeElement.querySelector(`#customMessage-${  this.id}`);

    // Use the last stored range if available
    if (this.lastRange) {
      try {
        // Check if the range is still valid
        if (container.contains(this.lastRange.commonAncestorContainer)) {
          // Insert the chip at the last cursor position
          this.lastRange.deleteContents();
          this.lastRange.insertNode(ionChip);

          // Move cursor after the chip
          this.lastRange.setStartAfter(ionChip);
          this.lastRange.setEndAfter(ionChip);

          // Update the selection
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(this.lastRange);
        } else {
          // If last cursor position is not in container, append at the end
          this.renderer.appendChild(container, ionChip);
        }
      } catch (_e) {
        // If range is invalid, append at the end
        this.renderer.appendChild(container, ionChip);
      }
    } else {
      // If no last range, append at the end
      this.renderer.appendChild(container, ionChip);
    }

    this.parseAndEmitInnerHtml();

    const foundPlaceholder = this.placeholders.find(
      (placeholder) => placeholder.value === field.value
    );
    if (!foundPlaceholder) {
      this.placeholders.push(field);
    } else {
      this.placeholders = this.placeholders.map((placeholder) => {
        if (placeholder.value === field.value) {
          return field;
        }
        return placeholder;
      });
    }
    this.placeholder = null;
    this.inputFocusOut.emit({ id: this.id, value: this.innerHtml });
  }

  parseChipMatch(match: string): string {
    if (!match) {
      return null;
    }
    let cleanWord = match.replace(/["']/g, '');
    cleanWord = cleanWord.replace(/[.,]$/, '');
    return `{{${cleanWord}}}`;
  }

  parseAndSetBody(): void {
    const { content, chips } = this.body;

    const container = this.el.nativeElement.querySelector(`#customMessage-${  this.id}`);
    this.renderer.setAttribute(container, "contenteditable", `${!this.readonly}`);
    container.innerHTML = "";

    // Check if content looks like JSON and applyFormatting is true
    if (this.isJsonContent(content)) {
      this.processJsonContent(content, chips, container);
    } else {
      // Original processing for non-JSON content
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, "text/html");

      // Function to process text and find chips
      const processText = (text: string) => {
        const words = text.split(" ");
        words.forEach((word) => {
          // Find all chip matches in the word
          const chipMatches = word.match(/{{([^}]+)}}/g);

          if (chipMatches && chipMatches.length > 0) {
            let remainingWord = word;

            chipMatches?.forEach((match) => {
              const bracketMatch = match?.match(/{{([^}]+)}}/);
              const parsedChipMatch = this.parseChipMatch(bracketMatch?.[1]);
              const chip = bracketMatch ? chips?.find((c) => c?.value === parsedChipMatch) : null;

              if (chip) {
                const matchIndex = remainingWord.indexOf(match);

                // Add text before the chip
                const beforeChip = remainingWord.substring(0, matchIndex);
                if (beforeChip && beforeChip.trim()) {
                  const textNode = this.renderer.createText(beforeChip);
                  this.renderer.appendChild(container, textNode);
                }

                // Add the chip
                this.appendPlaceholder(chip);

                // Update remaining word to process after this chip
                remainingWord = remainingWord.substring(matchIndex + match.length);
              }
            });

            // Add any remaining text after all chips
            if (remainingWord) {
              const textNode = this.renderer.createText(`${remainingWord  } `);
              this.renderer.appendChild(container, textNode);
            } else {
              const textNode = this.renderer.createText(' ');
              this.renderer.appendChild(container, textNode);
            }
          } else {
            const textNode = this.renderer.createText(`${word} `);
            this.renderer.appendChild(container, textNode);
          }
        });
      };

    // Process each node in the document
    const processNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        processText(node.textContent);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        // Create a new div for the element
        const newDiv = this.renderer.createElement(element.tagName.toLowerCase());

        // Copy all attributes
        Array.from(element.attributes).forEach(attr => {
          this.renderer.setAttribute(newDiv, attr.name, attr.value);
        });

        this.renderer.appendChild(container, newDiv);

        // Process all child nodes
        Array.from(node.childNodes).forEach(childNode => {
          if (childNode.nodeType === Node.TEXT_NODE) {
            processText(childNode.textContent);
          } else {
            processNode(childNode);
          }
        });
      }
    };

    // Process all nodes in the document body
    Array.from(doc.body.childNodes).forEach(processNode);
    }

    this.parseAndEmitInnerHtml();
    this.body = null;
  }

  /**
   * Check if content looks like JSON
   */
  private isJsonContent(content: string): boolean {
    if (!content || typeof content !== 'string') {
      return false;
    }

    const trimmed = content.trim();
    if (trimmed.startsWith('{{') && trimmed.endsWith('}}')) {
      return false;
    }
    return (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
           (trimmed.startsWith('[') && trimmed.endsWith(']'));
  }

  /**
   * Process JSON content while preserving formatting
   */
  private processJsonContent(content: string, chips: ChipPlaceholder[], container: any): void {
    try {
      // Parse JSON to validate and format it
      const parsed = JSON.parse(content);
      const formattedJson = JSON.stringify(parsed, null, 2);

      // Create a pre element to preserve formatting
      const preElement = this.renderer.createElement('pre');
      preElement.style.margin = '0';
      preElement.style.whiteSpace = 'pre-wrap';
      preElement.style.fontFamily = 'inherit';
      preElement.style.fontSize = 'inherit';

      // Process the formatted JSON to handle any chips
      const lines = formattedJson.split('\n');
      lines.forEach((line, index) => {
        // Check if line contains chip placeholders
        const chipMatches = line.match(/{{([^}]+)}}/g);

        if (chipMatches) {
          // Process line with chips
          let processedLine = line;
          chipMatches.forEach(match => {
            const parsedChipMatch = this.parseChipMatch(match.replace(/{{|}}/g, ''));
            const chip = chips.find((c) => c.value === parsedChipMatch);

            if (chip) {
              // Replace the placeholder with a chip
              const chipElement = this.createChipElement(chip);
              const textNode = this.renderer.createText(processedLine.substring(0, processedLine.indexOf(match)));
              this.renderer.appendChild(preElement, textNode);
              this.renderer.appendChild(preElement, chipElement);
              processedLine = processedLine.substring(processedLine.indexOf(match) + match.length);
            }
          });

          if (processedLine) {
            const textNode = this.renderer.createText(processedLine);
            this.renderer.appendChild(preElement, textNode);
          }
        } else {
          // Regular line without chips
          const textNode = this.renderer.createText(line);
          this.renderer.appendChild(preElement, textNode);
        }

        // Add line break if not the last line
        if (index < lines.length - 1) {
          const brElement = this.renderer.createElement('br');
          this.renderer.appendChild(preElement, brElement);
        }
      });

      this.renderer.appendChild(container, preElement);
    } catch (_error) {
      // If JSON parsing fails, fall back to regular text processing
      const textNode = this.renderer.createText(content);
      this.renderer.appendChild(container, textNode);
    }
  }

  /**
   * Create a chip element for JSON content
   */
  private createChipElement(chip: ChipPlaceholder): any {
    const ionChip = this.renderer.createElement("ion-chip");
    const span = this.renderer.createElement("span");
    const text = this.renderer.createText(chip.text);
    this.renderer.appendChild(span, text);
    this.renderer.appendChild(ionChip, span);

    if (!this.readonly) {
      const ionIcon = this.renderer.createElement("ion-icon");
      this.renderer.setAttribute(ionIcon, "name", "close-circle");
      this.renderer.listen(ionIcon, "click", () => this.removePlaceholder(ionChip));
      this.renderer.appendChild(ionChip, ionIcon);
    } else {
      this.renderer.addClass(ionChip, "fix-padding");
    }

    this.renderer.setAttribute(ionChip, "contenteditable", "false");
    this.renderer.setAttribute(ionChip, "id", chip.value);
    this.renderer.addClass(ionChip, "custom-chip");

    return ionChip;
  }
}
